import { useEffect } from 'react';
import i18n from '@lib/i18n';

import { useLocalStorage } from '../lib/useLocalStorage';
import { loadWithAuthGuard } from '../authentication/authErrorHandler';
import { TokenStoreContextValue } from '../authentication/TokenStore';
import { PlatformConfig, K12Locale, FoundryLang } from './platformConfig';

interface UseLanguagesParams {
  tokenStore: TokenStoreContextValue;
  localesUrl: string | null;
  signOutUrl: string;
  normalizeLocale: PlatformConfig['normalizeLocale'];
}

/**
 * Fetches available languages. No-op when localesUrl is null (foundry admin).
 * Locale normalization is delegated to normalizeLocale from platformConfig.
 */
export const useLanguages = ({ tokenStore, localesUrl, signOutUrl, normalizeLocale }: UseLanguagesParams) => {
  const [languages, setLanguages] = useLocalStorage('languages', { en: 'English' });

  useEffect(() => {
    if (!localesUrl) return;

    const urlWithoutLang = window.location.pathname.replace(`/${i18n.language}/`, '/');

    async function fetchLocales() {
      const items = await loadWithAuthGuard<K12Locale[] | FoundryLang[]>(localesUrl!, tokenStore, signOutUrl);
      if (items?.length) {
        setLanguages(Object.fromEntries(items.map(item => normalizeLocale(item, urlWithoutLang))));
      }
    }

    fetchLocales();
  }, [tokenStore, localesUrl, signOutUrl, normalizeLocale, setLanguages]);

  return { languages };
};
